import React from 'react';
import { Platform, TouchableOpacity, View, StyleSheet } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Ionicons } from '@expo/vector-icons';
import ThemedText from './ThemedText';
import { useTheme } from '../../theme/ThemeContext';

interface TimePickerProps {
  value: Date | undefined;
  onChange: (time: Date | undefined) => void;
  label: string;
  helperText?: string;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  label,
  helperText,
}) => {
  const { theme } = useTheme();
  const [showPicker, setShowPicker] = React.useState(false);

  const handleWebTimeChange = (event: any) => {
    const timeString = event.target.value;
    if (timeString) {
      const [hours, minutes] = timeString.split(':');
      const newTime = new Date();
      newTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      onChange(newTime);
    } else {
      onChange(undefined);
    }
  };

  const handleNativeTimeChange = (event: any, selectedTime?: Date) => {
    if (Platform.OS === 'android') {
      setShowPicker(false);
    }
    if (event.type === 'set' && selectedTime) {
      onChange(selectedTime);
    } else if (event.type === 'dismissed') {
      setShowPicker(false);
    }
  };

  const formatTimeForInput = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatTimeDisplay = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        <View style={styles.labelContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            {label}
          </ThemedText>
        </View>
        
        <View style={[styles.inputWrapper, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Ionicons name="time-outline" size={20} color={theme.textSecondary} style={styles.icon} />
          <input
            type="time"
            value={value ? formatTimeForInput(value) : ''}
            onChange={handleWebTimeChange}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              color: theme.text,
              fontSize: 16,
              fontFamily: 'inherit',
              paddingRight: 12,
            }}
          />
        </View>

        {helperText && (
          <ThemedText style={[styles.helperText, { color: theme.textSecondary }]}>
            {helperText}
          </ThemedText>
        )}
      </View>
    );
  }

  // Native (iOS/Android)
  return (
    <View style={styles.container}>
      <View style={styles.labelContainer}>
        <ThemedText style={[styles.label, { color: theme.text }]}>
          {label}
        </ThemedText>
      </View>
      
      <TouchableOpacity
        style={[styles.inputWrapper, { backgroundColor: theme.card, borderColor: theme.border }]}
        onPress={() => setShowPicker(true)}
      >
        <Ionicons name="time-outline" size={20} color={theme.textSecondary} style={styles.icon} />
        <ThemedText style={[styles.displayText, { color: value ? theme.text : theme.textSecondary }]}>
          {value ? formatTimeDisplay(value) : 'Select time'}
        </ThemedText>
      </TouchableOpacity>

      {helperText && (
        <ThemedText style={[styles.helperText, { color: theme.textSecondary }]}>
          {helperText}
        </ThemedText>
      )}

      {showPicker && (
        <DateTimePicker
          value={value || new Date()}
          mode="time"
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          onChange={handleNativeTimeChange}
        />
      )}

      {Platform.OS === 'ios' && showPicker && (
        <TouchableOpacity
          style={[styles.doneButton, { backgroundColor: theme.accent }]}
          onPress={() => setShowPicker(false)}
        >
          <ThemedText style={styles.doneButtonText}>Done</ThemedText>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  labelContainer: {
    marginBottom: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 48,
  },
  icon: {
    marginRight: 8,
  },
  displayText: {
    fontSize: 16,
    flex: 1,
  },
  helperText: {
    fontSize: 12,
    marginTop: 4,
  },
  doneButton: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  doneButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});

export default TimePicker;
